import LOCALS from 'commons/locals';
import type { TFunction } from 'i18next';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { CascaderOption } from 'types/base';
import {
  PmsProductWantRequestFormValues,
  PmsProductWantRequestOperateType,
  PmsProductWantRequestRecord,
  PmsProductWantRequestSaveDto,
  PmsProductWantRequestSource,
  PmsProductWantRequestStatus,
} from 'types/pms-product-want-request';

export const PMS_PRODUCT_WANT_REQUEST_STATUS_META: {
  value: PmsProductWantRequestStatus;
  labelKey: string;
  color: string;
}[] = [
  {
    value: PmsProductWantRequestStatus.PENDING_FOLLOWUP,
    labelKey: LOCALS.pwr_status_pending_followup,
    color: 'default',
  },
  {
    value: PmsProductWantRequestStatus.RECOMMENDED,
    labelKey: LOCALS.pwr_status_recommended,
    color: 'processing',
  },
  {
    value: PmsProductWantRequestStatus.DEAL_CLOSED,
    labelKey: LOCALS.pwr_status_deal_closed,
    color: 'success',
  },
  {
    value: PmsProductWantRequestStatus.CANCELLED,
    labelKey: LOCALS.pwr_status_cancelled,
    color: 'error',
  },
];

export const PMS_PRODUCT_WANT_REQUEST_SOURCE_META: {
  value: PmsProductWantRequestSource;
  labelKey: string;
}[] = [
  { value: PmsProductWantRequestSource.ADMIN, labelKey: LOCALS.pwr_source_admin },
  { value: PmsProductWantRequestSource.APP, labelKey: LOCALS.pwr_source_app },
  {
    value: PmsProductWantRequestSource.WEBSITE,
    labelKey: LOCALS.pwr_source_website,
  },
];

export const getWantRequestSourceSelectOptions = (t: TFunction) =>
  PMS_PRODUCT_WANT_REQUEST_SOURCE_META.map(({ value, labelKey }) => ({
    value,
    label: t(labelKey),
  }));

export const getWantRequestSourceLabel = (
  source: PmsProductWantRequestSource,
  t: TFunction,
) => {
  const option = PMS_PRODUCT_WANT_REQUEST_SOURCE_META.find(
    (item) => item.value === source,
  );
  return option ? t(option.labelKey) : source;
};

export const getWantRequestStatusSelectOptions = (t: TFunction) =>
  PMS_PRODUCT_WANT_REQUEST_STATUS_META.map(({ value, labelKey, color }) => ({
    value,
    label: t(labelKey),
    color,
  }));

export const findWantRequestStatusOption = (
  status: PmsProductWantRequestStatus,
) => PMS_PRODUCT_WANT_REQUEST_STATUS_META.find((item) => item.value === status);

export const getWantRequestOperateTypeLabel = (
  type: PmsProductWantRequestOperateType,
  t: TFunction,
) => {
  const labelKeyMap: Record<PmsProductWantRequestOperateType, string> = {
    [PmsProductWantRequestOperateType.CREATE]: LOCALS.pwr_operate_create,
    [PmsProductWantRequestOperateType.UPDATE]: LOCALS.modify,
    [PmsProductWantRequestOperateType.CANCEL]: LOCALS.cancel,
    [PmsProductWantRequestOperateType.DELETE]: LOCALS.delete,
    [PmsProductWantRequestOperateType.SEND_EMAIL]: LOCALS.pwr_operate_send_email,
  };
  const key = labelKeyMap[type];
  return key ? t(key) : type;
};

export const formatWantRequestStatusChange = (
  fromStatus: PmsProductWantRequestStatus | null,
  toStatus: PmsProductWantRequestStatus | null,
  t: TFunction,
) => {
  const statusLabel = (status: PmsProductWantRequestStatus) => {
    const option = findWantRequestStatusOption(status);
    return option ? t(option.labelKey) : status;
  };
  if (fromStatus && toStatus) {
    return `${statusLabel(fromStatus)} → ${statusLabel(toStatus)}`;
  }
  if (toStatus) {
    return statusLabel(toStatus);
  }
  return '-';
};

/** 商品分类 Cascader 路径（各级均为数字 id） */
export const parseCategoryCascaderPath = (treeIds: string): number[] =>
  treeIds
    .split(',')
    .filter((segment) => segment !== '')
    .map((segment) => Number(segment))
    .filter((n) => !Number.isNaN(n));

/** 材质 Cascader 路径（叶子为字符串 value，不可统一 Number()） */
export const parseMaterialCascaderPath = (
  treeIds: string,
): (string | number)[] =>
  treeIds
    .split(',')
    .filter((segment) => segment !== '')
    .map((segment) => {
      const n = Number(segment);
      return Number.isNaN(n) ? segment : n;
    });

export const toSaveDto = (
  values: PmsProductWantRequestFormValues,
): PmsProductWantRequestSaveDto => {
  const productCategoryId =
    values.productCategoryIdList[values.productCategoryIdList.length - 1];
  const materials = (values.materialPaths ?? [])
    .map((path) => String(path[path.length - 1]))
    .filter(Boolean);

  return {
    memberId: values.memberId,
    email: values.email?.trim() || undefined,
    phone: values.phone?.trim() || undefined,
    productCategoryId: Number(productCategoryId),
    ranks: values.ranks?.length ? values.ranks : undefined,
    color: values.color?.length ? values.color : undefined,
    hardware: values.hardware?.length ? values.hardware : undefined,
    stamp: values.stamp?.length ? values.stamp : undefined,
    materials: materials.length ? materials : undefined,
    remark: values.remark,
    status: values.status,
  };
};

export const recordToFormValues = (
  record: PmsProductWantRequestRecord,
  productCategoryCascaderOptions: CascaderOption[],
  materialCascaderOptions: CascaderOption[],
): PmsProductWantRequestFormValues => {
  const categoryLeaf = findCascaderOptionById(
    record.productCategoryId,
    productCategoryCascaderOptions,
  );
  const productCategoryIdList = categoryLeaf?.treeIds
    ? parseCategoryCascaderPath(categoryLeaf.treeIds)
    : [record.productCategoryId];

  const materialPaths: (string | number)[][] = [];
  (record.materials ?? []).forEach((leaf) => {
    const target = findCascaderOptionById(leaf, materialCascaderOptions);
    if (target?.treeIds) {
      materialPaths.push(parseMaterialCascaderPath(target.treeIds));
    } else {
      materialPaths.push([leaf]);
    }
  });

  return {
    memberId: record.memberId ?? undefined,
    email: record.email || undefined,
    phone: record.phone ?? undefined,
    productCategoryIdList,
    ranks: record.ranks ?? undefined,
    color: record.color ?? undefined,
    hardware: record.hardware ?? undefined,
    stamp: record.stamp ?? undefined,
    materialPaths,
    remark: record.remark ?? undefined,
    status: record.status ?? PmsProductWantRequestStatus.PENDING_FOLLOWUP,
  };
};

/** 根据分类叶子节点解析材质 Cascader 树（与商品编辑页一致） */
export const getMaterialOptionsForCategory = (
  productCategoryId: number,
  productCategoryCascaderOptions: CascaderOption[],
  materialCascaderOptionsMap: Record<string, CascaderOption[]>,
): CascaderOption[] => {
  const categoryLeaf = findCascaderOptionById(
    productCategoryId,
    productCategoryCascaderOptions,
  );
  const firstLevelKey = categoryLeaf?.treeIds?.split(',')[0];
  if (!firstLevelKey) return [];
  return (
    materialCascaderOptionsMap[firstLevelKey] ??
    materialCascaderOptionsMap[String(firstLevelKey)] ??
    []
  );
};
